import { useState, useEffect } from 'react';
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from 'utils/helpers';

/**
 * Custom hook for managing localStorage values with React state
 * @param {string} key - The localStorage key
 * @param {any} initialValue - The initial value to use if no value is found in localStorage
 * @returns {[any, function, function]} - [storedValue, setValue, removeValue]
 */
const useLocalStorage = (key, initialValue = null) => {
  // Get the initial value from localStorage or use the provided initialValue
  const [storedValue, setStoredValue] = useState(() => {
    const valueFromStorage = getLocalStorageItem(key, initialValue);
    return valueFromStorage === null ? initialValue : valueFromStorage;
  });

  // Update state when key changes
  useEffect(() => {
    const valueFromStorage = getLocalStorageItem(key, initialValue);
    setStoredValue(valueFromStorage === null ? initialValue : valueFromStorage);
  }, [key, initialValue]);

  // Update localStorage when the value changes
  useEffect(() => {
    if (storedValue !== null) {
      setLocalStorageItem(key, storedValue);
    }
  }, [key, storedValue]);

  // Function to update the value in state and localStorage
  const setValue = (value) => {
    // Allow value to be a function for the same API as useState
    const valueToStore = value instanceof Function ? value(storedValue) : value;

    // Update state
    setStoredValue(valueToStore);

    // Update localStorage directly to ensure immediate persistence
    setLocalStorageItem(key, valueToStore);
  };

  // Function to remove the value from state and localStorage
  const removeValue = () => {
    setStoredValue(null);
    removeLocalStorageItem(key);
  };

  return [storedValue, setValue, removeValue];
};

export default useLocalStorage;
